// LOOP CALENDAR SELECTOR FAST STABLE V22 — cache-first, merge-only refresh; never replace owner rows with worker-only partial results.
// LOOP AGENDA ENTRY SPEED V12A — worker calendar selector uses cache-first, bounded lookups, and quiet index fallbacks.
// LOOP AGENDA MADRE WORKER ACCESS MIRROR V86 — selector preserves enriched worker profile from mirror/employee.
// LOOP AGENDA MADRE WORKER HYDRATION RACE V85 — preserve real worker route/access profile when employees collection is denied or late.
// LOOP AGENDA MADRE WORKER RESOURCE PARITY V84 — selector passes worker profile/settings for same agenda resource on every platform.
 // LOOP AGENDA MADRE WORKER PERMISSIONS EXACT V80 — selector preserves business logo context for worker agenda routes.
// src/components/WorkerBusinessesModal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { collection, collectionGroup, getDoc, getDocs, doc, query, where, onSnapshot } from "firebase/firestore";
import { isCalendarBusinessTrashed } from "../utils/businessTrash";
import { db, auth } from "../firebase";
import getUserBusinessesAndRoles, { getCachedUserBusinesses } from "../services/userBusinesses";
import { getManualAuth, isManualAuthLikelyValid } from "../utils/manualAuth";
import { isIosCapacitor } from "../utils/nativeHttp";
import fsClient from "../data/firestoreClient";

const BRAND = "#00675f";
const WBM_CACHE_PREFIX = "loop_worker_businesses_modal_cache_v5_trash";
const WBM_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 12; // 12 horas
const WBM_LEGACY_KEYS = [
  "loop_worker_businesses_modal_cache_v2",
  "loop_worker_businesses_modal_cache_ts_v2",
];

function normalizeIdentityEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function getModalEffectiveUser() {
  try {
    const current = auth && auth.currentUser ? auth.currentUser : null;
    if (!isIosCapacitor() && current && (current.uid || current.email)) {
      return {
        uid: current.uid ? String(current.uid) : "",
        email: normalizeIdentityEmail(current.email || ""),
      };
    }
  } catch (_) {}

  try {
    if (isIosCapacitor()) {
      const manualAuth = getManualAuth();
      if (manualAuth && isManualAuthLikelyValid(manualAuth) && (manualAuth.uid || manualAuth.email)) {
        return {
          uid: manualAuth.uid ? String(manualAuth.uid) : "",
          email: normalizeIdentityEmail(manualAuth.email || ""),
        };
      }
    }
  } catch (_) {}

  return null;
}

function modalIdentityKey(userLike) {
  const uid = userLike && userLike.uid ? String(userLike.uid).trim() : "";
  const email = normalizeIdentityEmail(userLike && userLike.email ? userLike.email : "");
  if (!uid && !email) return "";
  return `${uid || "no_uid"}:${email || "no_email"}`;
}

function modalCacheKeyForIdentity(identityKey) {
  return identityKey ? `${WBM_CACHE_PREFIX}:${identityKey}` : "";
}

function clearLegacyModalCache() {
  try {
    WBM_LEGACY_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch (_) {}
}

function readModalCacheForIdentity(identityKey) {
  try {
    if (!identityKey) return [];
    clearLegacyModalCache();
    const raw = localStorage.getItem(modalCacheKeyForIdentity(identityKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed && parsed.savedAt);
    if (!savedAt || Date.now() - savedAt > WBM_CACHE_MAX_AGE_MS) return [];
    const items = Array.isArray(parsed.items) ? parsed.items : [];
    return items;
  } catch (_) {
    return [];
  }
}

function writeModalCacheForIdentity(identityKey, list) {
  try {
    if (!identityKey || !Array.isArray(list)) return;
    clearLegacyModalCache();
    localStorage.setItem(modalCacheKeyForIdentity(identityKey), JSON.stringify({
      items: list.slice(0, 30),
      savedAt: Date.now(),
    }));
  } catch (_) {}
}


const WBM_SPEED_DEBUG_KEY = "__LOOP_WBM_SPEED_DEBUG__";
const WBM_INDEX_SKIP_PREFIX = "loop_wbm_skip_employee_cg_field_v12a:";

function wbmSpeedDebug(stage, extra = {}) {
  try {
    if (typeof window === "undefined") return;
    const row = { stage, at: new Date().toISOString(), ...extra };
    const prev = Array.isArray(window[WBM_SPEED_DEBUG_KEY]) ? window[WBM_SPEED_DEBUG_KEY] : [];
    window[WBM_SPEED_DEBUG_KEY] = [...prev.slice(-30), row];
  } catch (_) {}
}

function wbmWithTimeout(promise, ms = 2500, label = "wbm_timeout") {
  let timer = null;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(label)), ms);
    }),
  ]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

function wbmIsTimeoutError(err) {
  const message = String(err?.message || err || "").toLowerCase();
  return message.includes("wbm_") && message.includes("timeout");
}

function wbmIsMissingIndexError(err) {
  const message = String(err?.message || err || "").toLowerCase();
  const code = String(err?.code || "").toLowerCase();
  return code.includes("failed-precondition") || message.includes("requires") && message.includes("index") || message.includes("create_exemption");
}

function wbmShouldSkipEmployeeCollectionGroupField(field) {
  try {
    if (!field) return false;
    const saved = sessionStorage.getItem(`${WBM_INDEX_SKIP_PREFIX}${field}`);
    return saved === "1";
  } catch (_) {
    return false;
  }
}

function wbmRememberMissingIndexField(field) {
  try {
    if (!field) return;
    sessionStorage.setItem(`${WBM_INDEX_SKIP_PREFIX}${field}`, "1");
  } catch (_) {}
}

function wbmBusinessListLimit() {
  return isIosCapacitor() ? 90 : 140;
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 21h18" />
      <path d="M5 21V7l7-4 7 4v14" />
      <path d="M9 21v-6h6v6" />
      <path d="M9 9h.01" />
      <path d="M15 9h.01" />
      <path d="M9 12h.01" />
      <path d="M15 12h.01" />
    </svg>
  );
}

function WorkerIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21a8 8 0 1 0-16 0" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <path d="M3.5 9.5h17" />
      <path d="M5.5 5h13A2.5 2.5 0 0 1 21 7.5v11A2.5 2.5 0 0 1 18.5 21h-13A2.5 2.5 0 0 1 3 18.5v-11A2.5 2.5 0 0 1 5.5 5Z" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function normalizeRole(item) {
  const role = String(item?.role || item?.calendarRole || item?.userRole || "").toLowerCase();

  const isWorker =
    role.includes("worker") ||
    role.includes("employee") ||
    role.includes("trabajador") ||
    Boolean(item?.employeeId || item?.employeeEmail || item?.workerId || item?.workerEmail);

  return isWorker ? "worker" : "owner";
}

// LOOP CALENDAR SELECTOR NAME GUARD V61
// LOOP WBM OUTSIDE CLICK CLOSE V62
function looksLikeFirebaseBusinessId(value = "") {
  const text = String(value || "").trim();
  return /^[A-Za-z0-9_-]{16,}$/.test(text) && !/[\sñÑáéíóúÁÉÍÓÚ#&.]/.test(text);
}

function cleanBusinessName(value, businessId = "") {
  const text = String(value || "").trim();
  const id = String(businessId || "").trim();
  if (!text) return "";
  if (id && text.toLowerCase() === id.toLowerCase()) return "";
  if (looksLikeFirebaseBusinessId(text)) return "";
  return text;
}

function getEntryBusinessName(item = {}, businessId = "") {
  const candidates = [
    item?.businessName,
    item?.name,
    item?.title,
    item?.displayName,
    item?.publicName,
    item?.business?.name,
    item?.business?.businessName,
    item?.raw?.businessName,
    item?.raw?.name,
    item?.raw?.title,
    item?.raw?.business?.name,
  ];

  for (const value of candidates) {
    const clean = cleanBusinessName(value, businessId);
    if (clean) return clean;
  }

  return "";
}

function getEntryBusinessLogo(item = {}) {
  const candidates = [
    item?.businessLogoUrl,
    item?.businessLogo,
    item?.logoUrl,
    item?.logoURL,
    item?.logo,
    item?.imageUrl,
    item?.photoUrl,
    item?.business?.logoUrl,
    item?.business?.logoURL,
    item?.business?.logo,
    item?.business?.imageUrl,
    item?.raw?.businessLogoUrl,
    item?.raw?.businessLogo,
    item?.raw?.logoUrl,
    item?.raw?.logoURL,
    item?.raw?.logo,
    item?.raw?.imageUrl,
    item?.raw?.business?.logoUrl,
    item?.raw?.business?.logo,
  ];

  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (value && typeof value === "object") {
      const nested = value.url || value.downloadURL || value.src || value.href || "";
      if (typeof nested === "string" && nested.trim()) return nested.trim();
    }
  }

  return "";
}


function getEntryWorkerName(item = {}) {
  const candidates = [
    item?.workerName,
    item?.employeeName,
    item?.workerDisplayName,
    item?.employeeDisplayName,
    item?.displayName,
    item?.fullName,
    item?.employee?.name,
    item?.worker?.name,
    item?.raw?.workerName,
    item?.raw?.employeeName,
    item?.raw?.name,
    item?.raw?.displayName,
    item?.raw?.fullName,
    item?.raw?.employee?.name,
    item?.raw?.worker?.name,
  ];
  for (const value of candidates) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function getEntryWorkerPhoto(item = {}) {
  const candidates = [
    item?.workerPhotoUrl,
    item?.employeePhotoUrl,
    item?.photoUrl,
    item?.photoURL,
    item?.imageUrl,
    item?.avatarUrl,
    item?.profileImageUrl,
    item?.profilePhotoUrl,
    item?.employee?.photoUrl,
    item?.worker?.photoUrl,
    item?.raw?.workerPhotoUrl,
    item?.raw?.employeePhotoUrl,
    item?.raw?.photoUrl,
    item?.raw?.photoURL,
    item?.raw?.imageUrl,
    item?.raw?.avatarUrl,
    item?.raw?.profileImageUrl,
    item?.raw?.profilePhotoUrl,
    item?.raw?.employee?.photoUrl,
    item?.raw?.worker?.photoUrl,
  ];
  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (value && typeof value === "object") {
      const nested = value.url || value.downloadURL || value.src || value.href || "";
      if (typeof nested === "string" && nested.trim()) return nested.trim();
    }
  }
  return "";
}

function getEntryCalendarSettings(item = {}) {
  const candidates = [
    item?.calendarSettings,
    item?.businessCalendarSettings,
    item?.agendaSettings,
    item?.business?.calendarSettings,
    item?.raw?.calendarSettings,
    item?.raw?.businessCalendarSettings,
    item?.raw?.agendaSettings,
    item?.raw?.business?.calendarSettings,
  ];
  for (const value of candidates) {
    if (value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0) return value;
  }
  return null;
}

function getEntryWorkerAvailability(item = {}) {
  const candidates = [
    item?.availability, item?.employeeAvailability, item?.workerAvailability, item?.schedule, item?.workingHours, item?.hours,
    item?.raw?.availability, item?.raw?.employeeAvailability, item?.raw?.workerAvailability, item?.raw?.schedule, item?.raw?.workingHours, item?.raw?.hours,
    item?.employee?.availability, item?.worker?.availability,
  ];
  for (const value of candidates) {
    if (value && typeof value === "object" && Object.keys(value).length > 0) return value;
  }
  return null;
}

function displayBusinessName(item = {}) {
  const businessId = item?.businessId || item?.id || "";
  return cleanBusinessName(item?.businessName || item?.name || item?.title, businessId) || "Cargando negocio...";
}

function normalizeEntry(item) {
  const businessId = String(item?.businessId || item?.id || "").trim();
  return {
    businessId,
    businessName: getEntryBusinessName(item, businessId),
    businessLogoUrl: getEntryBusinessLogo(item),
    businessLogo: getEntryBusinessLogo(item),
    logoUrl: getEntryBusinessLogo(item),
    employeeEmail: item?.employeeEmail || item?.workerEmail || item?.email || "",
    employeeId: item?.employeeId || item?.workerId || null,
    workerName: getEntryWorkerName(item),
    employeeName: getEntryWorkerName(item),
    workerPhotoUrl: getEntryWorkerPhoto(item),
    employeePhotoUrl: getEntryWorkerPhoto(item),
    calendarSettings: getEntryCalendarSettings(item),
    availability: getEntryWorkerAvailability(item),
    schedule: getEntryWorkerAvailability(item),
    workingHours: getEntryWorkerAvailability(item),
    role: normalizeRole(item),
    action: item?.action || null,
    raw: item?.raw || item || null,
  };
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function entryDedupeKey(entry) {
  const n = normalizeEntry(entry);
  const businessId = String(n.businessId || "").trim();
  if (!businessId) return "";

  // Calendar selector should show a business only once per section.
  // Worker access can arrive from several sources for the same business:
  // root workerAccessEmails, mirror docs and employees collectionGroup.
  // Those paths may disagree on employeeId/email, but they are the same calendar.
  if (n.role === "worker") return `worker:${businessId}`;
  return `owner:${businessId}`;
}

function mergeEntry(existing, next) {
  if (!existing) return normalizeEntry(next);
  const a = normalizeEntry(existing);
  const b = normalizeEntry(next);
  return {
    ...a,
    ...b,
    businessId: b.businessId || a.businessId,
    businessName: cleanBusinessName(b.businessName, b.businessId || a.businessId) || cleanBusinessName(a.businessName, a.businessId || b.businessId) || "",
    businessLogoUrl: b.businessLogoUrl || a.businessLogoUrl || b.businessLogo || a.businessLogo || "",
    businessLogo: b.businessLogo || a.businessLogo || b.businessLogoUrl || a.businessLogoUrl || "",
    logoUrl: b.logoUrl || a.logoUrl || b.businessLogoUrl || a.businessLogoUrl || "",
    role: a.role === "owner" || b.role === "owner" ? "owner" : (b.role || a.role || "worker"),
    // Prefer the concrete employees/{employeeId} match when one exists,
    // but keep the email fallback for old businesses/routes.
    employeeId: b.employeeId || a.employeeId || null,
    employeeEmail: normalizeEmail(b.employeeEmail || a.employeeEmail || "") || null,
    workerName: b.workerName || a.workerName || b.employeeName || a.employeeName || "",
    employeeName: b.employeeName || a.employeeName || b.workerName || a.workerName || "",
    workerPhotoUrl: b.workerPhotoUrl || a.workerPhotoUrl || b.employeePhotoUrl || a.employeePhotoUrl || "",
    employeePhotoUrl: b.employeePhotoUrl || a.employeePhotoUrl || b.workerPhotoUrl || a.workerPhotoUrl || "",
    calendarSettings: b.calendarSettings || a.calendarSettings || null,
    availability: b.availability || a.availability || null,
    schedule: b.schedule || a.schedule || b.availability || a.availability || null,
    workingHours: b.workingHours || a.workingHours || b.availability || a.availability || null,
    action: b.action || a.action || null,
    raw: { ...(a.raw && typeof a.raw === "object" ? a.raw : {}), ...(b.raw && typeof b.raw === "object" ? b.raw : {}) },
  };
}

function dedupeEntries(entries) {
  const map = new Map();

  (entries || []).forEach((entry) => {
    const n = normalizeEntry(entry);
    const key = entryDedupeKey(n);
    if (!key) return;
    map.set(key, mergeEntry(map.get(key), n));
  });

  return Array.from(map.values()).sort((a, b) => {
    if (a.role === b.role) return String(a.businessName || "").localeCompare(String(b.businessName || ""));
    return a.role === "owner" ? -1 : 1;
  });
}

function mergeVisibleCalendarEntriesV22(previous = [], incoming = []) {
  const prev = Array.isArray(previous) ? previous : [];
  const next = Array.isArray(incoming) ? incoming : [];
  if (!prev.length) return dedupeEntries(next);
  if (!next.length) return dedupeEntries(prev);
  // Order matters: dedupeEntries merges later rows into earlier rows, so incoming
  // can enrich names/photos/employeeIds without deleting owner rows that were already visible.
  return dedupeEntries([...prev, ...next]);
}

function toParentBusinessRowsV22(list = []) {
  return dedupeEntries(list).map((r) => ({
    id: r.businessId,
    businessId: r.businessId,
    name: cleanBusinessName(r.businessName, r.businessId) || "",
    businessName: cleanBusinessName(r.businessName, r.businessId) || "",
    businessLogoUrl: r.businessLogoUrl || r.businessLogo || r.logoUrl || "",
    businessLogo: r.businessLogo || r.businessLogoUrl || r.logoUrl || "",
    logoUrl: r.logoUrl || r.businessLogoUrl || r.businessLogo || "",
    role: r.role,
    employeeId: r.employeeId || null,
    employeeEmail: r.employeeEmail || null,
    workerName: r.workerName || r.employeeName || "",
    employeeName: r.employeeName || r.workerName || "",
    workerPhotoUrl: r.workerPhotoUrl || r.employeePhotoUrl || "",
    employeePhotoUrl: r.employeePhotoUrl || r.workerPhotoUrl || "",
    calendarSettings: r.calendarSettings || null,
    raw: r.raw || null,
  }));
}

function publishBusinessRowsV22(onBusinessesFetched, list = []) {
  if (typeof onBusinessesFetched !== "function") return;
  try { onBusinessesFetched(toParentBusinessRowsV22(list)); } catch (_) {}
}

async function fetchBusinessMetaSafe(businessId) {
  if (!businessId) return { name: "", logo: "" };

  const readFromData = (data, id = businessId) => {
    const source = { ...(data || {}), id, businessId: id };
    return {
      name: getEntryBusinessName(source, id),
      logo: getEntryBusinessLogo(source),
    };
  };

  try {
    if (isIosCapacitor() && fsClient && typeof fsClient.getDocByPath === "function") {
      const data = await fsClient.getDocByPath(`businesses/${businessId}`, { db });
      const meta = readFromData(data, businessId);
      if (meta.name || meta.logo) return meta;
    }
  } catch (_) {}

  try {
    const snap = await getDoc(doc(db, "businesses", businessId));
    if (snap && snap.exists()) {
      const data = snap.data() || {};
      const meta = readFromData(data, snap.id);
      if (meta.name || meta.logo) return meta;
    }
  } catch (_) {}

  return { name: "", logo: "" };
}

async function fetchBusinessNameSafe(businessId) {
  const meta = await fetchBusinessMetaSafe(businessId);
  return meta.name || "";
}

async function hydrateEntriesWithBusinessNames(entries = []) {
  const base = dedupeEntries(entries);
  const missing = base.filter((entry) => entry?.businessId && (!cleanBusinessName(entry.businessName, entry.businessId) || !getEntryBusinessLogo(entry)));
  if (!missing.length) return base;

  const metaMap = new Map();
  const uniqueIds = Array.from(new Set(missing.map((entry) => String(entry.businessId)).filter(Boolean))).slice(0, 30);
  let cursor = 0;
  const concurrency = Math.min(5, uniqueIds.length || 1);

  async function worker() {
    while (cursor < uniqueIds.length) {
      const idx = cursor;
      cursor += 1;
      const businessId = uniqueIds[idx];
      // eslint-disable-next-line no-await-in-loop
      const meta = await fetchBusinessMetaSafe(businessId);
      if (meta.name || meta.logo) metaMap.set(businessId, meta);
    }
  }

  await Promise.all(Array.from({ length: concurrency }, worker));

  return base.map((entry) => {
    const meta = metaMap.get(entry.businessId) || {};
    const logo = entry.businessLogoUrl || entry.businessLogo || entry.logoUrl || meta.logo || "";
    return {
      ...entry,
      businessName: cleanBusinessName(entry.businessName, entry.businessId) || meta.name || "",
      businessLogoUrl: logo,
      businessLogo: entry.businessLogo || logo,
      logoUrl: entry.logoUrl || logo,
    };
  });
}


async function getWorkerBusinessesByEmailWeb(email) {
  const targetEmail = normalizeEmail(email);
  if (!targetEmail) return [];

  const results = [];
  const fields = ["email", "employeeEmail", "workerEmail"];

  for (const field of fields) {
    if (wbmShouldSkipEmployeeCollectionGroupField(field)) {
      wbmSpeedDebug("skip_missing_index_employee_field_v12a", { field });
      continue;
    }

    try {
      const qBuilt = query(collectionGroup(db, "employees"), where(field, "==", targetEmail));
      const snap = await wbmWithTimeout(
        getDocs(qBuilt),
        field === "email" ? 1800 : 950,
        `wbm_employee_${field}_timeout`
      );

      for (const d of snap.docs) {
        const parts = d.ref.path.split("/");
        const bIdx = parts.indexOf("businesses");
        const businessId = bIdx >= 0 && parts.length > bIdx + 1 ? parts[bIdx + 1] : null;
        if (!businessId) continue;

        const data = d.data() || {};
        const businessMeta = await fetchBusinessMetaSafe(businessId);

        results.push({
          businessId,
          businessName: businessMeta.name,
          businessLogoUrl: businessMeta.logo || getEntryBusinessLogo({ raw: { business: data?.business || null }, ...data }),
          employeeId: d.id,
          employeeEmail: normalizeEmail(data.email || data.employeeEmail || data.workerEmail || targetEmail),
          workerName: getEntryWorkerName(data),
          employeeName: getEntryWorkerName(data),
          workerPhotoUrl: getEntryWorkerPhoto(data),
          employeePhotoUrl: getEntryWorkerPhoto(data),
          calendarSettings: getEntryCalendarSettings(data),
          availability: getEntryWorkerAvailability(data),
          schedule: getEntryWorkerAvailability(data),
          workingHours: getEntryWorkerAvailability(data),
          role: "worker",
          raw: { ...data, id: d.id },
        });
      }
    } catch (e) {
      if (wbmIsMissingIndexError(e)) {
        wbmRememberMissingIndexField(field);
        wbmSpeedDebug("missing_index_employee_field_v12a", { field });
        continue;
      }
      if (wbmIsTimeoutError(e)) {
        wbmSpeedDebug("timeout_employee_field_v12a", { field });
        continue;
      }
      console.warn(`[WorkerBusinessesModal] worker lookup by ${field} failed`, e);
    }
  }

  return dedupeEntries(results);
}

function businessRootHasWorkerEmailIOS(biz, targetEmail) {
  const fields = [
    biz?.workerAccessEmails,
    biz?.workerEmails,
    biz?.employeeEmails,
    biz?.staffEmails,
    biz?.accessEmails,
    biz?.allowedEmails,
    biz?.teamEmails,
  ];
  return fields.some((value) => {
    if (!value) return false;
    if (Array.isArray(value)) return value.map(normalizeEmail).includes(targetEmail);
    if (typeof value === "object") return Boolean(value[targetEmail]);
    return normalizeEmail(value) === targetEmail;
  });
}

async function getWorkerBusinessesByEmailIOS(email) {
  const targetEmail = normalizeEmail(email);
  if (!targetEmail) return [];

  const results = [];

  try {
    const businessesRaw = await wbmWithTimeout(
      fsClient.listCollectionByPath("businesses", {}, { db }),
      3200,
      "wbm_ios_businesses_timeout"
    );
    const businesses = (Array.isArray(businessesRaw) ? businessesRaw : []).slice(0, wbmBusinessListLimit());

    for (const biz of businesses) {
      const businessId = biz?.id || biz?.businessId || null;
      if (!businessId) continue;

      if (businessRootHasWorkerEmailIOS(biz, targetEmail)) {
        results.push({
          businessId,
          businessName: biz?.name || biz?.businessName || biz?.title || businessId,
          businessLogoUrl: getEntryBusinessLogo(biz),
          businessLogo: getEntryBusinessLogo(biz),
          employeeId: null,
          employeeEmail: targetEmail,
          role: "worker",
          raw: biz,
        });
      }

      let employees = [];
      try {
        employees = await wbmWithTimeout(
          import("../data/firestoreClient").then((m) =>
            m.default.listCollectionByPath(`businesses/${businessId}/employees`, {}, { db })
          ),
          650,
          "wbm_ios_employees_timeout"
        );
      } catch (innerErr) {
        if (!wbmIsTimeoutError(innerErr)) {
          wbmSpeedDebug("ios_employee_scan_skipped_v12a", { businessId, reason: innerErr?.message || String(innerErr) });
        }
        employees = [];
      }

      for (const emp of Array.isArray(employees) ? employees : []) {
        const empEmail = normalizeEmail(emp?.email || emp?.employeeEmail || emp?.workerEmail);
        if (!empEmail || empEmail !== targetEmail) continue;

        results.push({
          businessId,
          businessName: biz?.name || biz?.businessName || biz?.title || businessId,
          businessLogoUrl: getEntryBusinessLogo(biz),
          businessLogo: getEntryBusinessLogo(biz),
          employeeId: emp?.id || emp?.employeeId || null,
          employeeEmail: empEmail,
          workerName: getEntryWorkerName(emp),
          employeeName: getEntryWorkerName(emp),
          workerPhotoUrl: getEntryWorkerPhoto(emp),
          employeePhotoUrl: getEntryWorkerPhoto(emp),
          calendarSettings: getEntryCalendarSettings(emp),
          availability: getEntryWorkerAvailability(emp),
          schedule: getEntryWorkerAvailability(emp),
          workingHours: getEntryWorkerAvailability(emp),
          role: "worker",
          raw: emp,
        });
      }
    }
  } catch (e) {
    if (wbmIsTimeoutError(e)) {
      wbmSpeedDebug("ios_worker_lookup_timeout_v12a");
    } else {
      console.warn("[WorkerBusinessesModal] iOS worker lookup failed", e);
    }
  }

  return dedupeEntries(results);
}

function getSubtitle(item) {
  const role = normalizeRole(item);
  const email = item?.employeeEmail || item?.workerEmail || item?.email || "";
  const address = item?.address || item?.businessAddress || item?.raw?.address || "";

  if (role === "worker") {
    if (email && address) return `Trabajador · ${email} · ${address}`;
    if (email) return `Trabajador · ${email}`;
    return "Trabajador";
  }

  if (address) return address;
  return "Administrador";
}

function getPendingCount(item) {
  const count = Number(item?.pendingCount || item?.calendarPendingCount || item?.pending || 0);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

function SkeletonList() {
  return (
    <div className="wbm-list">
      <div className="wbm-skeleton" />
      <div className="wbm-skeleton" />
      <div className="wbm-skeleton" />
    </div>
  );
}

function EmptyState({ children }) {
  return <div className="wbm-empty">{children}</div>;
}

function BusinessRow({ item, type, onClick }) {
  const businessId = item.businessId || item.id || "";
  const name = displayBusinessName(item);
  const pendingCount = getPendingCount(item);

  return (
    <button type="button" className="wbm-card" onClick={() => onClick && onClick(item)}>
      <span className="wbm-card-icon">
        {type === "worker" ? <WorkerIcon /> : <BuildingIcon />}
      </span>

      <span className="wbm-card-main">
        <span className="wbm-card-title">{name}</span>
        <span className="wbm-card-subtitle">{getSubtitle(item)}</span>
      </span>

      <span className="wbm-card-right">
        {pendingCount > 0 ? <span className="wbm-badge">{pendingCount}</span> : null}
        <ArrowIcon />
      </span>
    </button>
  );
}

function BusinessSection({ title, description, icon, items, type, emptyText, onSelect }) {
  return (
    <section className="wbm-section">
      <div className="wbm-section-head">
        <div>
          <div className="wbm-section-title">
            {icon}
            <span>{title}</span>
          </div>
          {description ? <div className="wbm-section-desc">{description}</div> : null}
        </div>

        <div className="wbm-count">{items.length}</div>
      </div>

      {items.length === 0 ? (
        <EmptyState>{emptyText}</EmptyState>
      ) : (
        <div className="wbm-list">
          {items.map((item, idx) => (
            <BusinessRow
              key={`${item.role || "role"}-${item.businessId || item.id || "business"}`}
              item={item}
              type={type}
              onClick={onSelect}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function WorkerBusinessesModal({
  open = false,
  onClose = () => {},
  onSelect = null,
  businesses: propBusinesses = undefined,
  loading: propLoading = false,
  noBusinessesFound = false,
  noBusinessesMessage = null,
  onBusinessesFetched = null,
  autoOpenSingle = true,
}) {
  const selectEntryRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [sessionIdentity, setSessionIdentity] = useState(() => modalIdentityKey(getModalEffectiveUser()));
  useEffect(() => {
    const sync = () => setSessionIdentity(modalIdentityKey(getModalEffectiveUser()));
    const unsubscribe = auth.onAuthStateChanged(sync);
    window.addEventListener("manual-auth-changed", sync);
    window.addEventListener("manual-auth-cleared", sync);
    return () => {
      unsubscribe();
      window.removeEventListener("manual-auth-changed", sync);
      window.removeEventListener("manual-auth-cleared", sync);
    };
  }, []);
  const [items, setItems] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [removedBusinessIds, setRemovedBusinessIds] = useState(new Set());
  const lifecycleIds = [...new Set(items.map(row => row.businessId).filter(Boolean))].sort().join("|");
  useEffect(() => {
    if (!open || !lifecycleIds) return undefined;
    let active = true;
    const unsubscribers = lifecycleIds.split("|").map(id => onSnapshot(doc(db, "businesses", id), snap => {
      if (!active || (!snap.exists() && snap.metadata.fromCache)) return;
      const removed = !snap.exists() || isCalendarBusinessTrashed(snap.data());
      setRemovedBusinessIds(previous => {
        if (previous.has(id) === removed) return previous;
        const next = new Set(previous);
        if (removed) next.add(id); else next.delete(id);
        return next;
      });
    }, () => { /* A transient permission/network error is not evidence of deletion. */ }));
    return () => { active = false; unsubscribers.forEach(unsubscribe => unsubscribe()); };
  }, [open, lifecycleIds]);
  const [hasShownFastCache, setHasShownFastCache] = useState(false);
  const panelRef = useRef(null);
  const rootRef = useRef(null);
  const navigate = useNavigate();
  const [portalEl, setPortalEl] = useState(null);
  const itemsRef = useRef([]);
  const activeIdentityKeyRef = useRef("");

  useEffect(() => {
    itemsRef.current = Array.isArray(items) ? items : [];
  }, [items]);

  const replaceVisibleEntriesV22 = useCallback((next = []) => {
    const normalized = dedupeEntries(Array.isArray(next) ? next : []);
    itemsRef.current = normalized;
    setItems(normalized);
    return normalized;
  }, []);

  const commitVisibleEntriesV22 = useCallback((incoming = [], { identityKey = "", publish = false } = {}) => {
    const normalized = dedupeEntries(Array.isArray(incoming) ? incoming : []);
    if (!normalized.length) return itemsRef.current || [];

    const merged = mergeVisibleCalendarEntriesV22(itemsRef.current || [], normalized);
    itemsRef.current = merged;
    setItems(merged);

    if (identityKey && merged.length) writeModalCacheForIdentity(identityKey, merged);
    if (publish) publishBusinessRowsV22(onBusinessesFetched, merged);
    return merged;
  }, [onBusinessesFetched]);

  const requestClose = useCallback(() => {
    if (typeof onClose === "function") onClose();
  }, [onClose]);

  const stopModalEvent = useCallback((event) => {
    try {
      event.stopPropagation();
    } catch (_) {}
  }, []);

  const handleOutsidePointer = useCallback((event) => {
    try {
      const panel = panelRef.current;
      const target = event?.target || null;
      if (!panel || !target || !panel.contains(target)) {
        requestClose();
      }
    } catch (_) {
      requestClose();
    }
  }, [requestClose]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      setPortalEl(document.body);
    }
  }, []);

  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;

    const html = document.documentElement;
    const body = document.body;

    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyTouchAction = body.style.touchAction;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.touchAction = "none";

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.touchAction = prevBodyTouchAction;
    };
  }, [open]);

  useEffect(() => {
    let mounted = true;
    const effectiveUser = getModalEffectiveUser();
    const identityKey = modalIdentityKey(effectiveUser);
    const isCurrent = () => mounted && identityKey === modalIdentityKey(getModalEffectiveUser());
    activeIdentityKeyRef.current = identityKey;
    replaceVisibleEntriesV22([]);
    setHasShownFastCache(false);
    setLoadError(false);
    setRemovedBusinessIds(new Set());
    if (!open || !effectiveUser?.uid) {
      setLoading(false);
      return () => { mounted = false; };
    }
    setLoading(true);
    // Access lists must be authoritative. Old props/caches are not proof of
    // membership and an empty response must remove previously visible rows.
    (async () => {
      try {
        const rows = await wbmWithTimeout(getUserBusinessesAndRoles(db, effectiveUser), 12500, 'wbm_calendars_timeout');
        if (!isCurrent()) return;
        const normalized = dedupeEntries(rows.map(normalizeEntry));
        const calendars = normalized.filter(row => !isCalendarBusinessTrashed(row));
        if (autoOpenSingle && calendars.length === 1 && calendars[0].action !== 'analytics') {
          selectEntryRef.current(calendars[0]);
          return;
        }
        replaceVisibleEntriesV22(normalized);
        setLoading(false);
        const hydrated = await hydrateEntriesWithBusinessNames(normalized);
        if (!isCurrent()) return;
        replaceVisibleEntriesV22(hydrated);
      } catch (error) {
        if (!isCurrent()) return;
        replaceVisibleEntriesV22([]);
        setLoadError(true);
        console.warn("[WorkerBusinessesModal] Current account calendars unavailable", error);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [open, sessionIdentity, retryAttempt, autoOpenSingle, replaceVisibleEntriesV22]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") requestClose();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, requestClose]);

  function handleSelectEntry(it) {
    if (activeIdentityKeyRef.current !== modalIdentityKey(getModalEffectiveUser())) return;
    const normalized = normalizeEntry(it);
    if (removedBusinessIds.has(normalized.businessId) || isCalendarBusinessTrashed(normalized)) return;

    const sel = {
      id: normalized.businessId,
      businessId: normalized.businessId,
      businessName: cleanBusinessName(normalized.businessName, normalized.businessId) || "Cargando negocio...",
      businessLogoUrl: normalized.businessLogoUrl || normalized.businessLogo || normalized.logoUrl || "",
      businessLogo: normalized.businessLogo || normalized.businessLogoUrl || normalized.logoUrl || "",
      logoUrl: normalized.logoUrl || normalized.businessLogoUrl || normalized.businessLogo || "",
      employeeEmail: normalized.employeeEmail || "",
      employeeId: normalized.employeeId || null,
      workerName: normalized.workerName || normalized.employeeName || "",
      employeeName: normalized.employeeName || normalized.workerName || "",
      workerPhotoUrl: normalized.workerPhotoUrl || normalized.employeePhotoUrl || "",
      employeePhotoUrl: normalized.employeePhotoUrl || normalized.workerPhotoUrl || "",
      calendarSettings: normalized.calendarSettings || null,
      availability: normalized.availability || null,
      schedule: normalized.schedule || normalized.availability || null,
      workingHours: normalized.workingHours || normalized.availability || null,
      role: normalized.role || "owner",
      action: normalized.action || it?.action || null,
      raw: normalized.raw || null,
    };

    if (typeof onSelect === "function") {
      try {
        onSelect(sel);
        requestClose();
        return;
      } catch (e) {
        console.warn("[WBM] onSelect threw", e);
      }
    }

    const navState = {
      businessId: sel.businessId,
      businessName: sel.businessName,
      businessLogo: sel.businessLogo || sel.businessLogoUrl || sel.logoUrl || "",
      businessLogoUrl: sel.businessLogoUrl || sel.businessLogo || sel.logoUrl || "",
      logoUrl: sel.logoUrl || sel.businessLogoUrl || sel.businessLogo || "",
      role: sel.role || "owner",
      employeeId: sel.employeeId || null,
      employeeEmail: sel.employeeEmail || null,
      workerName: sel.workerName || sel.employeeName || "",
      employeeName: sel.employeeName || sel.workerName || "",
      workerPhotoUrl: sel.workerPhotoUrl || sel.employeePhotoUrl || "",
      employeePhotoUrl: sel.employeePhotoUrl || sel.workerPhotoUrl || "",
      calendarSettings: sel.calendarSettings || null,
      availability: sel.availability || null,
      schedule: sel.schedule || sel.availability || null,
      workingHours: sel.workingHours || sel.availability || null,
      raw: sel.raw || null,
      fromWorkerBusinessesModal: true,
    };

    if (sel.action === "analytics") {
      navigate(`/business/${sel.businessId}/analytics`, { state: navState });
    } else if (sel.role === "worker" && sel.employeeId) {
      navigate(`/business/${sel.businessId}/calendar?worker=${encodeURIComponent(sel.employeeId)}`, { state: navState });
    } else if (sel.role === "worker" && sel.employeeEmail) {
      navigate(`/business/${sel.businessId}/calendar?workerEmail=${encodeURIComponent(sel.employeeEmail)}`, { state: navState });
    } else {
      navigate(`/admin-booking-calendar/${sel.businessId}`, { state: navState });
    }

    requestClose();
  }

  selectEntryRef.current = handleSelectEntry;
  const showLoading = loading;
  const normalizedItems = activeIdentityKeyRef.current === modalIdentityKey(getModalEffectiveUser()) && Array.isArray(items) ? items.map(normalizeEntry).filter(item => !removedBusinessIds.has(item.businessId) && !isCalendarBusinessTrashed(item)) : [];
  const showSoftRefreshing = showLoading && normalizedItems.length > 0;

  const { ownedBusinesses, workerBusinesses } = useMemo(() => {
    const owned = [];
    const worker = [];

    normalizedItems.forEach((item) => {
      if (normalizeRole(item) === "worker") worker.push(item);
      else owned.push(item);
    });

    return { ownedBusinesses: owned, workerBusinesses: worker };
  }, [normalizedItems]);

  const hasAnyItems = ownedBusinesses.length > 0 || workerBusinesses.length > 0;
  const finalNoMessage = noBusinessesMessage || "No encontré empresas asociadas a tu usuario.";

  if (!open || !portalEl) return null;

  const modalContent = (
    <div
      ref={rootRef}
      className="wbm-root"
      aria-modal="true"
      role="dialog"
      aria-label="Selecciona calendario"
      data-loop-wbm-v62="outside-click-close"
      onMouseDown={handleOutsidePointer}
      onTouchStart={handleOutsidePointer}
    >
      <style>{`
        .wbm-root {
          position: fixed;
          inset: 0;
          width: 100vw;
          height: 100vh;
          height: 100dvh;
          z-index: 2147483000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
          box-sizing: border-box;
          overflow: hidden;
          isolation: isolate;
        }

        .wbm-backdrop {
          position: fixed;
          inset: 0;
          z-index: 0;
          border: 0;
          background: rgba(15,23,42,0.36);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          cursor: pointer;
          touch-action: manipulation;
        }

        .wbm-panel {
          position: relative;
          z-index: 1;
          width: min(100%, 620px);
          max-height: min(78dvh, 640px);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border-radius: 26px;
          background: #ffffff;
          border: 1px solid rgba(226,232,240,0.95);
          box-shadow: 0 28px 90px rgba(15,23,42,0.28);
          transform: translateZ(0);
        }

        .wbm-grabber {
          display: none;
          width: 42px;
          height: 5px;
          border-radius: 999px;
          background: #cbd5e1;
          margin: 10px auto 4px;
          flex: 0 0 auto;
        }

        .wbm-head {
          padding: 18px 20px 13px;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
        }

        .wbm-eyebrow {
          color: ${BRAND};
          font-size: 12px;
          font-weight: 950;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          margin-bottom: 8px;
        }

        .wbm-title {
          margin: 0;
          color: #0f172a;
          font-size: 24px;
          line-height: 1.04;
          font-weight: 950;
          letter-spacing: -0.05em;
        }

        .wbm-subtitle {
          margin: 6px 0 0;
          color: #64748b;
          font-size: 14px;
          line-height: 1.45;
          font-weight: 650;
          max-width: 560px;
        }

        .wbm-head-actions {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          flex: 0 0 auto;
        }

        .wbm-refreshing-pill {
          border-radius: 999px;
          border: 1px solid #ccfbf1;
          background: #ecfdf5;
          color: ${BRAND};
          padding: 6px 9px;
          font-size: 11px;
          font-weight: 950;
          white-space: nowrap;
        }

        .wbm-close {
          width: 38px;
          height: 38px;
          border-radius: 999px;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex: 0 0 auto;
          box-shadow: 0 10px 24px rgba(15,23,42,0.06);
        }

        .wbm-body {
          padding: 16px 20px 18px;
          overflow: auto;
          -webkit-overflow-scrolling: touch;
        }

        .wbm-section {
          margin-bottom: 16px;
        }

        .wbm-section:last-child {
          margin-bottom: 0;
        }

        .wbm-section-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
        }

        .wbm-section-title {
          color: #0f172a;
          font-size: 16px;
          font-weight: 950;
          letter-spacing: -0.03em;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .wbm-section-title svg {
          color: ${BRAND};
        }

        .wbm-section-desc {
          margin-top: 4px;
          color: #64748b;
          font-size: 13px;
          font-weight: 650;
          line-height: 1.4;
        }

        .wbm-count {
          color: #94a3b8;
          font-size: 12px;
          font-weight: 900;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 999px;
          padding: 4px 9px;
          flex: 0 0 auto;
        }

        .wbm-list {
          display: grid;
          gap: 8px;
        }

        .wbm-card {
          width: 100%;
          border: 1px solid rgba(226,232,240,0.95);
          background: #ffffff;
          border-radius: 18px;
          padding: 11px 12px;
          display: grid;
          grid-template-columns: 42px minmax(0, 1fr) auto;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          text-align: left;
          box-shadow: 0 12px 30px rgba(15,23,42,0.055);
          transition: transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
        }

        .wbm-card:hover {
          transform: translateY(-1px);
          border-color: rgba(0,103,95,0.28);
          box-shadow: 0 18px 38px rgba(15,23,42,0.08);
          background: #fcfffe;
        }

        .wbm-card-icon {
          width: 42px;
          height: 42px;
          border-radius: 15px;
          background: rgba(0,103,95,0.08);
          color: ${BRAND};
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .wbm-card-main {
          min-width: 0;
        }

        .wbm-card-title {
          display: block;
          color: #0f172a;
          font-size: 15px;
          font-weight: 950;
          letter-spacing: -0.025em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .wbm-card-subtitle {
          display: block;
          color: #64748b;
          font-size: 13px;
          font-weight: 650;
          line-height: 1.35;
          margin-top: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .wbm-card-right {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #94a3b8;
        }

        .wbm-badge {
          min-width: 22px;
          height: 22px;
          padding: 0 7px;
          border-radius: 999px;
          background: ${BRAND};
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 950;
        }

        .wbm-empty {
          border: 1px dashed #cbd5e1;
          background: #f8fafc;
          color: #64748b;
          border-radius: 22px;
          padding: 16px;
          font-size: 14px;
          line-height: 1.55;
          font-weight: 700;
        }

        .wbm-skeleton {
          height: 64px;
          border-radius: 18px;
          background: linear-gradient(90deg, #f1f5f9 0%, #f8fafc 50%, #f1f5f9 100%);
          background-size: 200% 100%;
          animation: wbmShimmer 1.25s infinite linear;
        }

        @keyframes wbmShimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }

        @media (max-width: 720px) {
          .wbm-root {
            align-items: flex-end;
            padding: 0;
          }

          .wbm-panel {
            position: fixed;
            left: 0;
            right: 0;
            bottom: 0;
            width: 100%;
            max-height: calc(88dvh - env(safe-area-inset-bottom, 0px));
            border-radius: 26px 26px 0 0;
            border-left: 0;
            border-right: 0;
            border-bottom: 0;
          }

          .wbm-grabber {
            display: block;
          }

          .wbm-head {
            padding: 12px 16px 12px;
          }

          .wbm-title {
            font-size: 22px;
          }

          .wbm-refreshing-pill {
            display: none;
          }

          .wbm-subtitle {
            font-size: 13px;
          }

          .wbm-body {
            padding: 16px 16px calc(22px + env(safe-area-inset-bottom, 0px));
          }

          .wbm-card {
            grid-template-columns: 44px minmax(0, 1fr) auto;
            padding: 13px;
            border-radius: 20px;
          }

          .wbm-card-icon {
            width: 44px;
            height: 44px;
            border-radius: 16px;
          }
        }


        /* =========================================================
           LOOP WBM — ANDROID NAV SAFE BOTTOM V30
           The modal opened from the Calendario header button was ending at
           bottom:0, so Android navigation controls could cover the bottom rows.
           Header already exposes --loop-safe-bottom as max(48px, 34px) in the
           real Android WebView. Use it here instead of raw env() only.
           ========================================================= */
        @media (max-width: 720px) {
          body.is-mobile-app .wbm-root {
            --wbm-safe-bottom: var(--loop-safe-bottom, max(env(safe-area-inset-bottom, 0px), 34px));
            --wbm-safe-side: 10px;
            align-items: flex-end !important;
            justify-content: center !important;
            padding: 0 var(--wbm-safe-side) calc(var(--wbm-safe-bottom) + 10px) !important;
            overflow: hidden !important;
          }

          body.is-mobile-app .wbm-backdrop {
            bottom: 0 !important;
          }

          body.is-mobile-app .wbm-panel {
            left: var(--wbm-safe-side) !important;
            right: var(--wbm-safe-side) !important;
            bottom: calc(var(--wbm-safe-bottom) + 8px) !important;
            width: auto !important;
            max-width: calc(100vw - (var(--wbm-safe-side) * 2)) !important;
            max-height: calc(100dvh - var(--loop-mobile-header-total, 94px) - var(--wbm-safe-bottom) - 20px) !important;
            border-radius: 26px !important;
            border: 1px solid rgba(226,232,240,0.95) !important;
            box-shadow: 0 24px 80px rgba(15,23,42,0.30) !important;
          }

          body.is-mobile-app .wbm-body {
            padding-bottom: 24px !important;
          }
        }

        @media (max-width: 380px) {
          body.is-mobile-app .wbm-root {
            --wbm-safe-side: 8px;
          }

          body.is-mobile-app .wbm-panel {
            max-height: calc(100dvh - var(--loop-mobile-header-total, 94px) - var(--wbm-safe-bottom) - 14px) !important;
          }
        }

      `}</style>

      <button
        type="button"
        className="wbm-backdrop"
        aria-label="Cerrar selector de calendario"
        onMouseDown={(e) => {
          e.stopPropagation();
          requestClose();
        }}
        onTouchStart={(e) => {
          e.stopPropagation();
          requestClose();
        }}
        onClick={(e) => {
          e.stopPropagation();
          requestClose();
        }}
      />

      <section
        ref={panelRef}
        className="wbm-panel"
        onMouseDown={stopModalEvent}
        onTouchStart={stopModalEvent}
        onClick={stopModalEvent}
      >
        <div className="wbm-grabber" aria-hidden="true" />

        <header className="wbm-head">
          <div>
            <div className="wbm-eyebrow">Calendario</div>
            <h2 className="wbm-title">Selecciona un calendario</h2>
            <p className="wbm-subtitle">
              Elige una empresa que administras o una empresa donde trabajas.
            </p>
          </div>

          <div className="wbm-head-actions">
            {showSoftRefreshing ? <span className="wbm-refreshing-pill">Actualizando...</span> : null}
            <button type="button" className="wbm-close" onClick={() => onClose && onClose()} aria-label="Cerrar">
              <CloseIcon />
            </button>
          </div>
        </header>

        <div className="wbm-body">
          {showLoading && normalizedItems.length === 0 ? (
            <div role="status" aria-live="polite" style={{display:'flex',alignItems:'center',gap:12,padding:'16px 4px',color:'#536c68'}}>
              <span aria-hidden="true" style={{width:18,height:18,border:'2px solid #d8e7e4',borderTopColor:BRAND,borderRadius:'50%',animation:'wbm-spin 0.8s linear infinite',flexShrink:0}} />
              <span>Buscando tus calendarios…</span>
              <style>{'@keyframes wbm-spin{to{transform:rotate(360deg)}} @media(prefers-reduced-motion:reduce){.wbm-body [role="status"] span{animation:none!important}}'}</style>
            </div>
          ) : hasAnyItems ? (
            <>
              {ownedBusinesses.length > 0 ? (
                <BusinessSection
                  title="Empresas"
                  description="Calendarios de negocios que administras."
                  icon={<BuildingIcon />}
                  items={ownedBusinesses}
                  type="owner"
                  emptyText=""
                  onSelect={handleSelectEntry}
                />
              ) : null}

              {workerBusinesses.length > 0 ? (
                <BusinessSection
                  title="Trabajas en"
                  description="Calendarios donde estás agregado como trabajador."
                  icon={<WorkerIcon />}
                  items={workerBusinesses}
                  type="worker"
                  emptyText=""
                  onSelect={handleSelectEntry}
                />
              ) : null}
            </>
          ) : (
            <EmptyState>
              {loadError ? <div role="alert">
                <p>No pudimos cargar tus calendarios.</p>
                <p style={{fontWeight:400}}>Comprueba tu conexión e inténtalo de nuevo.</p>
                <button type="button" onClick={() => setRetryAttempt(value => value + 1)} style={{marginTop:12,padding:'10px 18px',border:0,borderRadius:10,background:BRAND,color:'#fff',cursor:'pointer'}}>Reintentar</button>
              </div> : finalNoMessage}
            </EmptyState>
          )}
        </div>
      </section>
    </div>
  );

  return createPortal(modalContent, portalEl);
}
